import React, { createContext, useContext, useMemo } from 'react';
import type {
  EnumCutsheetAnimalSpecies,
  EnumCutsheetInspectionLevels,
  ProcessorCapability,
  ProcessorSettings,
} from 'lib/graphql';

export type WizardConfig = {
  capabilities?: ProcessorCapability[];
  settings?: ProcessorSettings;
  animalSpecies?: EnumCutsheetAnimalSpecies;
  inspectionLevel?: EnumCutsheetInspectionLevels;
  activeSpecification?: ProcessorCapability['activeSpecification'];
};

const WizardConfigContext = createContext<WizardConfig | undefined>(undefined);

export function WizardConfigProvider({
  children,
  capabilities,
  settings,
  animalSpecies,
  inspectionLevel,
}: React.PropsWithChildren<WizardConfig>) {
  const value = useMemo(
    () => ({
      capabilities,
      settings,
      animalSpecies,
      inspectionLevel,
      activeSpecification: capabilities?.find(
        (cap: ProcessorCapability) =>
          cap.animalSpecies === animalSpecies &&
          cap.inspectionLevel === inspectionLevel,
      )?.activeSpecification,
    }),
    [capabilities, settings, animalSpecies, inspectionLevel],
  );

  return (
    <WizardConfigContext.Provider value={value}>
      {children}
    </WizardConfigContext.Provider>
  );
}

export function useWizardConfig(): WizardConfig {
  const ctx = useContext(WizardConfigContext);
  if (!ctx) {
    throw new Error(
      'useWizardConfig must be used within a WizardConfigProvider',
    );
  }
  return ctx;
}
